import React, { Component, PropTypes } from 'react';
import { DayType } from './DayType';

class Day extends Component {

  /**
   * Renders empty day for the calendar - placeholder for months not starting on
   * Monday and/or ending on Sunday.
   *
   * @return {JSX.Element} empty day
   * @private
   */
  _generateEmptyDay = () => {
    return (
        <div aria-disabled="true" className="DayPicker-Day text-body DayPicker-Day--disabled DayPicker-Day--outside" />
    );
  }

  /**
   * Renders past days in the mont.
   *
   * @param day the number of the day
   * @param topLevelStyling top-level styling applicable to all days
   * @return {JSX.Element} past day
   * @private
   */
  _generateUnavailablePreviousDay = (day, topLevelStyling) => {
    const { styling } = this.props;

    const backgroundColor = _.get(styling, 'date.unavailable.background');
    const fontColor = _.get(styling, 'date.unavailable.color');

    const styleOverride = {
      ...(!!backgroundColor && {background: backgroundColor}),
      ...(!!fontColor && {color: fontColor})
    };

    return (
        <div className="DayPicker-Day text-body DayPicker-Day--disabled" role="button" style={topLevelStyling}>
          <div className="shs-widgets--calendar--day-container p-1 shs-widgets--calendar--day__undefined day-disabled text-muted" style={styleOverride}>
            <span className="shs-widgets--calendar--day__number day" style={this._getDayTextSize(styling)}>{day}</span>
          </div>
        </div>
    );
  }

  /**
   * Renders selected start day of the reservation.
   *
   * @param day the number of the day
   * @param price price for the room on that day
   * @param topLevelStyling top-level styling applicable to all days
   * @param shouldHaveTooltip a flag to decide whether a tooltip should display
   * @return {JSX.Element} reservation start day
   * @private
   */
  _generateSelectedStartDay = (day, price, topLevelStyling, shouldHaveTooltip = true) => {
    const { styling } = this.props;

    const backgroundColor = _.get(styling, 'date.selected.background');
    const fontColor = _.get(styling, 'date.selected.color');

    const styleOverride = {
      ...(!!backgroundColor && {background: backgroundColor}),
      ...(!!fontColor && {color: fontColor})
    };

    const tooltipBackgroundColor = _.get(styling, 'tooltip.background');
    const tooltipFontColor = _.get(styling, 'tooltip.color');
    const tooltipBorderColor = _.get(styling, 'tooltip.border');

    const tooltipStyleOverride = {
      ...(!!tooltipBackgroundColor && {background: tooltipBackgroundColor}),
      ...(!!tooltipFontColor && {color: tooltipFontColor}),
      // border is 1px solid in SBE by default, only color changes
      ...(!!tooltipBorderColor && {border: `${tooltipBorderColor} 1px solid`})
    };

    return (
          <div className="DayPicker-Day text-body start DayPicker-Day--selected DayPicker-Day--today" role="button" style={topLevelStyling}>
            <div className="shs-widgets--calendar--day-container p-1 shs-widgets--calendar--day__undefined bg-primary text-white rounded-left selected" style={styleOverride}>
              {shouldHaveTooltip && (
                  <span className="shs-widgets--calendar--day__tooltip shs-widgets--calendar--day__tooltip__price badge badge-dark badge-pill d-block" style={tooltipStyleOverride}>
                    {/* Sets background color on tooltip's caret tip pseudo-element */}
                    <style dangerouslySetInnerHTML={{
                      __html: [
                        '.shs-widgets--calendar--day__tooltip:after {',
                        `  background: ${tooltipBackgroundColor};`,
                        `  border-bottom: ${tooltipBorderColor} 1px solid;`,
                        `  border-right: ${tooltipBorderColor} 1px solid;`,
                        '}'
                      ].join('\n')
                    }}>
                  </style>
                    <span style={this._getPriceTextSize(styling)}>{price}</span>
                  </span>
              )}
              <span className="shs-widgets--calendar--day__number" style={this._getDayTextSize(styling)}>{day}</span>
              <span className="shs-widgets--calendar--day__price" style={this._getPriceTextSize(styling)}>{price}</span>
            </div>
          </div>
    );
  }

  /**
   * Renders selected end day of the reservation.
   *
   * @param day the number of the day
   * @param topLevelStyling top-level styling applicable to all days
   * @return {JSX.Element} reservation end day
   * @private
   */
  _generateSelectedEndDay = (day, topLevelStyling) => {
    const { styling } = this.props;

    const backgroundColor = _.get(styling, 'date.selected.background');
    const fontColor = _.get(styling, 'date.selected.color');

    const styleOverride = {
      ...(!!backgroundColor && {background: backgroundColor}),
      ...(!!fontColor && {color: fontColor})
    };

    return (
        <div className="DayPicker-Day text-body end DayPicker-Day--selected" role="button" style={topLevelStyling}>
          <div className="shs-widgets--calendar--day-container p-1 shs-widgets--calendar--day__undefined bg-primary text-white rounded-right selected" style={styleOverride}>
            <span className="shs-widgets--calendar--day__number" style={this._getDayTextSize(styling)}>{day}</span>
          </div>
        </div>
    );
  }

  /**
   * Renders a day available to be booked.
   *
   * @param day the number of the day
   * @param price price for the room on that day
   * @param topLevelStyling top-level styling applicable to all days
   * @return {JSX.Element} available day
   * @private
   */
  _generateAvailableDay = (day, price, topLevelStyling) => {
    const { styling } = this.props;
    return (
        <div className="DayPicker-Day text-body " role="button" style={topLevelStyling}>
          <div className="shs-widgets--calendar--day-container p-1 shs-widgets--calendar--day__undefined">
            <span className="shs-widgets--calendar--day__number" style={this._getDayTextSize(styling)}>{day}</span>
            <span className="shs-widgets--calendar--day__price" style={this._getPriceTextSize(styling)}>{price}</span>
          </div>
        </div>
    );
  }

  /**
   * Renders unavailable future day.
   *
   * @param day the number of the day
   * @param topLevelStyling top-level styling applicable to all days
   * @return {JSX.Element} unavailable future day
   * @private
   */
  _renderUnavailableFutureDay = (day, topLevelStyling) => {
    const { styling } = this.props;

    const backgroundColor = _.get(styling, 'date.unavailableFutureDate.background');
    const fontColor = _.get(styling, 'date.unavailableFutureDate.textColor');
    const lineColor = _.get(styling, 'date.unavailableFutureDate.lineColor');

    const styleOverride = {
      ...(!!backgroundColor && {background: backgroundColor}),
      ...(!!fontColor && {color: fontColor})
    };

    return (
        <div className="DayPicker-Day text-body " role="button" style={topLevelStyling}>
          <div className="shs-widgets--calendar--day-container p-1 shs-widgets--calendar--day__NoAvailableInventory day-disabled text-muted day-disabled-restricted" style={styleOverride}>
            <svg className="shs-widgets--calendar--day__unavailable">
              <line x1="0" y1="0" x2="100%" y2="100%" style={{stroke: lineColor}}></line>
              <line x1="0" y1="100%" x2="100%" y2="0" style={{stroke: lineColor}}></line>
            </svg>
            <span className="shs-widgets--calendar--day__number" style={this._getDayTextSize(styling)}>{day}</span>
          </div>
        </div>
    );
  }

  /**
   * Renders a day when check-in is not permitted.
   *
   * @param day the number of the day
   * @param price price for the room on that day
   * @param topLevelStyling top-level styling applicable to all days
   * @return {JSX.Element} no check-in day
   * @private
   */
  _renderNoCheckInDay = (day, price, topLevelStyling) => {
    const { styling } = this.props;

    const background = _.get(styling, 'date.noCheckIn.background');
    const color = _.get(styling, 'date.noCheckIn.color');

    const styleOverride = {
      ...(!!background && {background: background}),
      ...(!!color && {color: color})
    };

    return (
        <div className="DayPicker-Day text-body " role="button" style={topLevelStyling}>
          <div className="shs-widgets--calendar--day-container p-1 shs-widgets--calendar--day__NoArrive" style={styleOverride}>
            <span className="shs-widgets--calendar--day__number" style={this._getDayTextSize(styling)}>{day}</span>
            <span className="shs-widgets--calendar--day__price" style={this._getPriceTextSize(styling)}>{price}</span>
          </div>
        </div>
    );
  }

  /**
   * Renders a day when check-out is not permitted.
   *
   * @param day the number of the day
   * @param price price for the room on that day
   * @param topLevelStyling top-level styling applicable to all days
   * @return {JSX.Element} no check-out day
   * @private
   */
  _renderNoCheckOutDay = (day, price, topLevelStyling) => {
    const { styling } = this.props;

    const background = _.get(styling, 'date.noCheckOut.background');
    const color = _.get(styling, 'date.noCheckOut.color');

    const styleOverride = {
      ...(!!background && {background: background}),
      ...(!!color && {color: color})
    };

    return (
        <div className="DayPicker-Day text-body " role="button" style={topLevelStyling}>
          <div className="shs-widgets--calendar--day-container p-1 shs-widgets--calendar--day__NoDeparture" style={styleOverride}>
            <span className="shs-widgets--calendar--day__number" style={this._getDayTextSize(styling)}>{day}</span>
            <span className="shs-widgets--calendar--day__price" style={this._getPriceTextSize(styling)}>{price}</span>
          </div>
        </div>
    );
  }

  /**
   * Renders a day when group code is available.
   *
   * @param day the number of the day
   * @param price price for the room on that day
   * @param topLevelStyling top-level styling applicable to all days
   * @return {JSX.Element} group code available day
   * @private
   */
  _generateGroupCodeAvailableDay = (day, price, topLevelStyling) => {
    const { styling } = this.props;

    const dotBorderColor = _.get(styling, 'dot.border');
    const dotInsideColor = _.get(styling, 'dot.color');

    return (
        <div className="DayPicker-Day text-body " role="button" style={topLevelStyling}>
          <div className="shs-widgets--calendar--day-container p-1 shs-widgets--calendar--day__undefined">
            {/* Sets the color of the inside of the dot */}
            <style dangerouslySetInnerHTML={{
              __html: [
                '.shs-widgets--calendar--day__allocation_indicator:after {',
                `  background: ${dotInsideColor === '' ? '#587817' : dotInsideColor};`,
                '}'
              ].join('\n')
            }} />
            <span className="shs-widgets--calendar--day__allocation_indicator" style={{ ...(!!dotBorderColor && { background: dotBorderColor }) }}></span>
            <span
                className="shs-widgets--calendar--day__number" style={this._getDayTextSize(styling)}>{day}</span>
            <span className="shs-widgets--calendar--day__price" style={this._getPriceTextSize(styling)}>{price}</span>
          </div>
        </div>
    );
  }

  /**
   * Renders a day with spinner.
   *
   * @param day the number of the day
   * @param topLevelStyling top-level styling applicable to all days
   * @return {JSX.Element} selected day with spinner
   * @private
   */
  _generateSelectedSpinnerDay = (day, topLevelStyling) => {
    const { styling } = this.props;

    const backgroundColor = _.get(styling, 'date.selected.background');
    const fontColor = _.get(styling, 'date.selected.color');

    const styleOverride = {
      ...(!!backgroundColor && {background: backgroundColor}),
      ...(!!fontColor && {color: fontColor})
    };

    const spinnerColor1 = _.get(styling, 'date.selected.spinner.color1')
    const spinnerColor2 = _.get(styling, 'date.selected.spinner.color2')

    const spinnerStyleOverride = {
      ...(!!spinnerColor1 && {
        borderTop: `3px solid ${spinnerColor1}`,
        borderRight: `3px solid ${spinnerColor1}`,
        borderBottom: `3px solid ${spinnerColor1}`
      }),
      ...(!!spinnerColor2 && {borderLeft: `3px solid ${spinnerColor2}`})
    }

    return (
        <div className="DayPicker-Day text-body DayPicker-Day--selected" role="button" style={topLevelStyling}>
          <div className="shs-widgets--calendar--day-container p-1 shs-widgets--calendar--day__undefined is-between bg-primary text-white selected" style={styleOverride}>
            <span className="shs-widgets--calendar--day__number">{day}</span>
            <span className="shs-widgets--calendar--day__loading">
              <div className="spinner_container">
                <div className="spinner_main" style={spinnerStyleOverride}>
                </div>
              </div>
            </span>
          </div>
        </div>
    );
  }

  /**
   * Gets top-level styling applicable for all days in the calendar. This is
   * retrieved from available dates.
   *
   * @return {{}}
   * @private
   */
  _getTopLevelDayStyling = () => {
    const { styling } = this.props;

    const backgroundColor = _.get(styling, 'date.available.background');
    const fontColor = _.get(styling, 'date.available.color');

    return {
      ...(!!backgroundColor && {background: backgroundColor}),
      ...(!!fontColor && {color: fontColor})
    };
  }

  _getDayTextSize = (styling) => {
    const size = _.get(styling, 'date.textSize');
    return {
      ...(!!size && {fontSize: size})
    };
  }

  _getPriceTextSize = (styling) => {
    const size = _.get(styling, 'inlinePrice.textSize');
    return {
      ...(!!size && {fontSize: size})
    };
  }

  render() {
    const {
      day,
      price,
      type
    } = this.props;

    const topLevelStyling = this._getTopLevelDayStyling();

    switch (type) {
      case DayType.Empty:
        return this._generateEmptyDay();
      case DayType.UnavailablePrevious:
        return this._generateUnavailablePreviousDay(day, topLevelStyling);
      case DayType.SelectedStart:
        return this._generateSelectedStartDay(day, price, topLevelStyling);
      case DayType.SelectedEnd:
        return this._generateSelectedEndDay(day, topLevelStyling);
      case DayType.Available:
        return this._generateAvailableDay(day, price, topLevelStyling);
      case DayType.UnavailableFuture:
        return this._renderUnavailableFutureDay(day, topLevelStyling);
      case DayType.NoCheckIn:
        return this._renderNoCheckInDay(day, price, topLevelStyling);
      case DayType.NoCheckOut:
        return this._renderNoCheckOutDay(day, price, topLevelStyling);
      case DayType.GroupCodeAvailable:
        return this._generateGroupCodeAvailableDay(day, price, topLevelStyling);
      case DayType.SelectedSpinner:
        return this._generateSelectedSpinnerDay(day, topLevelStyling);
      default:
        return null;
    }
  }
}

Day.propTypes = {
  /**
   * Number of the day. This is required for non-empty days.
   */
  day: PropTypes.string,
  /**
   * Day price including currency code/symbol. This is required for sellable
   * days.
   */
  price: PropTypes.string,
  /**
   * Type of the day being created.
   */
  type: PropTypes.oneOf(Object.values(DayType)).isRequired,
  /**
   * Styling
   */
  styling: PropTypes.shape({})
}

export default Day;


// WEBPACK FOOTER //
// ./src/components/pages/theme/calendar/Day.js